import { Close } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import AcxChip from "components/UI/AcxChip";
import { observer } from "mobx-react";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface AppliedFilterChipProps {
    disabled?: boolean;
    isApplicable: boolean;
    onRemove?(): void;
    id?: string;
    tooltipContent?: React.ReactNode;
}

function getPlainText(node: React.ReactNode): string {
    if (typeof node === "string" || typeof node === "number") {
        return `${node}`;
    }

    if (Array.isArray(node)) {
        return node.map(getPlainText).join("");
    }

    if (React.isValidElement(node)) {
        return getPlainText(node.props.children);
    }

    return "";
}

const AppliedFilterChipBase = observer(function (
    props: React.PropsWithChildren<AppliedFilterChipProps>,
) {
    const labelRef = useRef<HTMLDivElement | null>(null);
    const [isTextTruncated, setIsTextTruncated] = useState(false);
    const plainTextLabel = useMemo(
        () => getPlainText(props.children).trim(),
        [props.children],
    );

    useEffect(() => {
        const labelElement = labelRef.current;
        if (!labelElement) return;

        const updateTruncationState = () => {
            setIsTextTruncated(
                labelElement.scrollWidth > labelElement.clientWidth,
            );
        };

        updateTruncationState();

        if (typeof ResizeObserver === "undefined") return;

        const resizeObserver = new ResizeObserver(() => {
            updateTruncationState();
        });
        resizeObserver.observe(labelElement);

        return () => {
            resizeObserver.disconnect();
        };
    }, [plainTextLabel, props.onRemove]);

    let defaultTooltip: string | undefined;
    if (!props.isApplicable) defaultTooltip = "Not applied";
    else if (isTextTruncated) defaultTooltip = plainTextLabel;

    return (
        <Grid item>
            <AcxChip
                size="small"
                color={!props.isApplicable ? "gray" : "lightBlue"}
                label={
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        flexWrap="nowrap"
                    >
                        <Grid
                            item
                            ref={labelRef}
                            sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: "40ch",
                            }}
                        >
                            <span>{props.children}</span>
                        </Grid>
                        {props.onRemove && !props.disabled && (
                            <Grid item>
                                <IconButton
                                    size="small"
                                    color={
                                        props.isApplicable
                                            ? "primary"
                                            : "default"
                                    }
                                    onClick={() => props.onRemove?.()}
                                >
                                    <Close fontSize="small" />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                }
                customTooltip={props.tooltipContent ?? defaultTooltip}
                id={props.id}
            />
        </Grid>
    );
});

export default AppliedFilterChipBase;
